import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import toast, { Toaster } from 'react-hot-toast';
import * as XLSX from 'xlsx';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  ArrowLeft, User, Mail, Phone, MapPin, Calendar, Clock,
  Package, ShoppingCart, Trash2, Check, X, AlertTriangle, Download, MessageSquare,
  Ticket, FileText, RefreshCw
} from 'lucide-react';
import { userService } from '../services/userService';
import { curationService } from '../services/curationService';
import { assetService } from '../services/assetService';

const UserDetailPage = () => {
  const { userId } = useParams();
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  
  // Products data
  const [curationPackages, setCurationPackages] = useState([]);
  const [assets, setAssets] = useState([]);
  const [userPurchases, setUserPurchases] = useState({ assets: [], packages: [] });
  
  // NEW: Product interests from main website integration
  const [productInterests, setProductInterests] = useState([]);
  const [productInterestsLoading, setProductInterestsLoading] = useState(false);
  
  // NEW: Lili conversation summary
  const [liliData, setLiliData] = useState({
    lili_conversation_summary: null,
    lili_last_updated: null,
    lili_total_conversations: 0
  });
  const [liliDataLoading, setLiliDataLoading] = useState(false);
  
  // Delete confirmation
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [adminPassword, setAdminPassword] = useState('');
  const [deleteError, setDeleteError] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [showFinalConfirm, setShowFinalConfirm] = useState(false);

  useEffect(() => {
    fetchAllData();
  }, [userId]);

  const fetchAllData = async () => {
    setLoading(true);
    setError('');
    try {
      // Fetch user details
      const userData = await userService.getUserById(userId);
      console.log('Fetched user details:', userData);
      setUser(userData);

      // Fetch curation packages for "Products Interested" section
      const packagesData = await curationService.getAllPackages();
      setCurationPackages(packagesData);

      // Fetch assets
      const assetsData = await assetService.getAllAssets();
      setAssets(assetsData);

      // Fetch user's purchases
      try {
        const purchases = await userService.getUserPurchases(userId);
        // Ensure purchases has the expected structure
        setUserPurchases({
          assets: purchases?.assets || [],
          packages: purchases?.packages || []
        });
      } catch (purchaseErr) {
        console.error('Error fetching user purchases:', purchaseErr);
        // Set empty structure on error
        setUserPurchases({ assets: [], packages: [] });
      }

      // NEW: Fetch user's product interests from main website integration
      setProductInterestsLoading(true);
      try {
        const interests = await userService.getUserProductInterests(userId);
        setProductInterests(interests);
        console.log('Fetched product interests:', interests);
      } catch (interestErr) {
        console.warn('Could not fetch product interests:', interestErr);
        setProductInterests([]); // Fallback to empty array
      } finally {
        setProductInterestsLoading(false);
      }

      // NEW: Fetch Lili conversation summary
      setLiliDataLoading(true);
      try {
        const liliSummary = await userService.getUserLiliSummary(userId);
        setLiliData(liliSummary);
        console.log('Fetched Lili summary:', liliSummary);
      } catch (liliErr) {
        console.warn('Could not fetch Lili summary:', liliErr);
        setLiliData({
          lili_conversation_summary: null,
          lili_last_updated: null,
          lili_total_conversations: 0
        });
      } finally {
        setLiliDataLoading(false);
      }
    } catch (err) {
      console.error('Error fetching data:', err);
      setError(err.response?.data?.detail || 'Failed to load user details');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleString('en-IN', {
      day: '2-digit',
      month: 'long',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(amount);
  };

  const handleDeleteClick = () => {
    setShowFinalConfirm(false);
    setAdminPassword('');
    setDeleteError('');
    setShowDeleteDialog(true);
  };

  const handlePasswordSubmit = () => {
    // Validate password
    if (!adminPassword || adminPassword.trim().length === 0) {
      setDeleteError('Please enter your admin password');
      return;
    }
    // Show final confirmation
    setDeleteError('');
    setShowFinalConfirm(true);
  };

  const handleFinalDelete = async () => {
    setDeleting(true);
    setDeleteError('');
    
    try {
      console.log('🗑️ Attempting to delete user:', userId);
      const result = await userService.deleteUser(userId, adminPassword);
      
      // Success - close dialog and show success message
      setShowDeleteDialog(false);
      toast.success('User removed from admin panel successfully!', {
        duration: 3000,
        position: 'top-center',
      });
      
      // If there's a backend limitation note, show additional info
      if (result.note) {
        console.warn('Backend limitation:', result.note);
        setTimeout(() => {
          toast('Note: Backend deletion endpoint not available', {
            duration: 2000,
            position: 'top-center',
            icon: 'ℹ️',
          });
        }, 1000);
      }
      
      // Navigate back to users list after a short delay
      setTimeout(() => {
        navigate('/users');
      }, 1500);
      
    } catch (err) {
      console.error('❌ Error deleting user:', err);
      console.error('❌ Error response:', err.response?.data);
      console.error('❌ Error status:', err.response?.status);
      
      // Show error message
      let errorMsg = 'Failed to delete user. Please try again.';
      
      if (err.response?.status === 405) {
        errorMsg = 'User deletion not supported by backend. Contact system administrator.';
      } else if (err.response?.status === 401) {
        errorMsg = 'Incorrect admin password. Please try again.';
      } else if (err.response?.status === 404) {
        errorMsg = 'User not found or already deleted.';
      } else if (err.response?.data?.detail) {
        errorMsg = err.response.data.detail;
      } else if (err.message) {
        errorMsg = err.message;
      }
      
      setDeleteError(errorMsg);
      toast.error(errorMsg, {
        duration: 4000,
        position: 'top-center',
      });
      
      // Reset to password entry on auth errors
      if (err.response?.status === 401 || errorMsg.toLowerCase().includes('password')) {
        setShowFinalConfirm(false);
        setAdminPassword('');
      }
    } finally {
      setDeleting(false);
    }
  };

  const resetDeleteFlow = () => {
    setShowDeleteDialog(false);
    setShowFinalConfirm(false);
    setAdminPassword('');
    setDeleteError('');
    setDeleting(false);
  };

  // Check if user is interested in a package
  const isInterestedInPackage = (packageName) => {
    if (!user?.interest_form_data?.interested_360_packages) return false;
    return user.interest_form_data.interested_360_packages.includes(packageName);
  };

  // Get purchase info for an asset
  const getAssetPurchaseInfo = (assetId) => {
    return userPurchases.assets.find(p => p.asset_id === assetId);
  };

  // Check if user purchased a package
  const getPackagePurchaseInfo = (packageId) => {
    return userPurchases?.packages?.find(p => p.package_id === packageId);
  };

  // Enhanced getInterestedProducts function for main website integration
  const getInterestedProducts = () => {
    // If we have real product interests from main website, use those
    if (productInterests && productInterests.length > 0) {
      // Process interests to show only current state (not removed items)
      // Group by product_id to handle add/remove sequences
      const productStateMap = new Map();
      
      // Sort by timestamp to process in chronological order
      const sortedInterests = [...productInterests].sort((a, b) => 
        new Date(a.timestamp) - new Date(b.timestamp)
      );
      
      sortedInterests.forEach(interest => {
        const productId = interest.product_id;
        
        if (interest.interest_type === 'cart_addition') {
          // Add or update the product in cart
          productStateMap.set(productId, {
            name: interest.product_name,
            type: interest.product_type,
            interestType: interest.interest_type,
            timestamp: interest.timestamp,
            productId: interest.product_id,
            additionalData: interest.additional_data || {}
          });
        } else if (interest.interest_type === 'cart_removal') {
          // Remove the product from cart (if it was added)
          productStateMap.delete(productId);
        } else {
          // For other interest types (like eoi_submission), always include
          productStateMap.set(productId, {
            name: interest.product_name,
            type: interest.product_type,
            interestType: interest.interest_type,
            timestamp: interest.timestamp,
            productId: interest.product_id,
            additionalData: interest.additional_data || {}
          });
        }
      });
      
      // Return the current state of all products as array
      return Array.from(productStateMap.values());
    }

    // Fallback to EOI form data (existing logic for backward compatibility)
    const interested = [];
    
    // Check Individual Cars interest
    if (user.interest_form_data?.interested_individual_cars) {
      interested.push({
        name: 'Individual Cars',
        type: 'individual',
        interestType: 'eoi_submission',
        timestamp: user.created_at,
        productId: null,
        additionalData: {}
      });
    }
    
    // Check 360 packages interest
    if (user.interest_form_data?.interested_360_packages) {
      user.interest_form_data.interested_360_packages.forEach(packageName => {
        const pkg = curationPackages.find(p => p.name === packageName);
        if (pkg) {
          interested.push({
            name: `${pkg.name} Pack`,
            type: 'package',
            interestType: 'eoi_submission',
            timestamp: user.created_at,
            productId: pkg.id,
            additionalData: {}
          });
        }
      });
    }
    
    return interested;
  };

  // Helper function to format interest type with cart-specific handling
  const formatInterestType = (interestType) => {
    const types = {
      'cart_addition': '🛒 Added to Cart',
      'cart_removal': '🗑️ Removed from Cart',
      'checkout_started': '💳 Started Checkout',
      'eoi_submission': '📝 EOI Submission',
      'wishlist': '❤️ Added to Wishlist',
      'page_view': '👁️ Viewed Details',
      'quote_request': '💰 Requested Quote'
    };
    return types[interestType] || interestType;
  };

  // Helper function to format timestamp
  const formatTimestamp = (timestamp) => {
    if (!timestamp) return 'Unknown';
    return new Date(timestamp).toLocaleString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Excel download functionality
  const handleDownloadUserData = () => {
    const userData = {
      'User Information': {
        'Full Name': `${user.first_name} ${user.last_name}`,
        'Email': user.email,
        'Phone': user.phone || 'N/A',
        'Address': user.address || 'N/A',
        'City': user.city || 'N/A',
        'State': user.state || 'N/A',
        'Pincode': user.pincode || 'N/A',
        'Account Created': user.created_at ? new Date(user.created_at).toLocaleDateString() : 'N/A',
        'Last Visited': user.last_visited ? new Date(user.last_visited).toLocaleDateString() : new Date(user.created_at).toLocaleDateString(),
        'Profile Status': user.profile_completed ? 'Complete' : 'Incomplete',
        'Role': user.role || 'Member'
      }
    };

    // Add EOI Form Data if available
    if (user.interest_form_data) {
      userData['EOI Form Responses'] = {};
      Object.keys(user.interest_form_data).forEach(key => {
        const value = user.interest_form_data[key];
        const formattedKey = key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
        userData['EOI Form Responses'][formattedKey] = Array.isArray(value) ? value.join(', ') : value || 'N/A';
      });
    }

    // Enhanced Products Interested section for Excel with cart details
    const detailedInterests = getInterestedProducts();
    if (detailedInterests.length > 0) {
      userData['Detailed Product Interests'] = detailedInterests.reduce((acc, interest, index) => {
        acc[`Interest ${index + 1}`] = {
          'Product Name': interest.name,
          'Product Type': interest.type === 'asset' ? 'Individual Asset' : 
                         interest.type === 'package' ? 'Package' : 
                         interest.type === 'individual' ? 'Individual Cars' : interest.type,
          'Interest Type': formatInterestType(interest.interestType).replace(/[🛒🗑️💳📝❤️👁️💰]/g, '').trim(), // Remove emojis for Excel
          'Timestamp': formatTimestamp(interest.timestamp),
          'Price Shown': interest.additionalData?.price_shown ? `₹${(interest.additionalData.price_shown / 100000).toFixed(1)}L` : 'N/A',
          'Items Count': interest.additionalData?.items_count || 'N/A',
          'Tickets Interested': interest.additionalData?.tickets_interested || 'N/A',
          'Vehicles Included': interest.additionalData?.vehicles_included ? interest.additionalData.vehicles_included.join(', ') : 'N/A',
          'Investment Range': interest.additionalData?.investment_range || 'N/A'
        };
        return acc;
      }, {});
    } else {
      userData['Detailed Product Interests'] = { 'Note': 'No detailed interests recorded yet' };
    }

    // Add legacy Products Interested for backward compatibility
    const interestedProducts = [];
    if (user.interest_form_data?.interested_individual_cars) {
      interestedProducts.push('Individual Cars');
    }
    if (user.interest_form_data?.interested_360_packages) {
      interestedProducts.push(...user.interest_form_data.interested_360_packages);
    }
    
    userData['Legacy Products Interested'] = interestedProducts.length > 0 ? 
      interestedProducts.reduce((acc, item, index) => {
        acc[`Product ${index + 1}`] = item;
        return acc;
      }, {}) : 
      { 'Note': 'No products selected in legacy EOI form' };

    // Add Purchase History if available
    if (userPurchases?.assets?.length > 0 || userPurchases?.packages?.length > 0) {
      userData['Purchase History'] = {};
      
      userPurchases.assets?.forEach((purchase, index) => {
        userData['Purchase History'][`Asset Purchase ${index + 1}`] = 
          `Asset ID: ${purchase.asset_id}, Amount: ₹${purchase.amount}, Status: ${purchase.payment_status}`;
      });
      
      userPurchases.packages?.forEach((purchase, index) => {
        userData['Purchase History'][`Package Purchase ${index + 1}`] = 
          `Package ID: ${purchase.package_id}, Amount: ₹${purchase.amount}, Status: ${purchase.payment_status}`;
      });
    } else {
      userData['Purchase History'] = { 'Note': 'No purchases yet' };
    }

    // Create workbook and worksheets
    const wb = XLSX.utils.book_new();
    
    Object.keys(userData).forEach(sheetName => {
      const ws = XLSX.utils.json_to_sheet([userData[sheetName]]);
      XLSX.utils.book_append_sheet(wb, ws, sheetName);
    });

    // Download file
    const fileName = `User_${user.first_name}_${user.last_name}_Data_${new Date().toISOString().split('T')[0]}.xlsx`;
    XLSX.writeFile(wb, fileName);
    
    toast.success('User data downloaded successfully!', {
      duration: 3000,
      position: 'top-center',
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
          <p className="mt-4 text-gray-600">Loading user details...</p>
        </div>
      </div>
    );
  }

  if (error || !user) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-600 mb-4">{error || 'User not found'}</p>
          <Button onClick={() => navigate('/users')}>
            <ArrowLeft size={16} className="mr-2" />
            Back to Users
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Toast Notifications */}
      <Toaster />
      
      {/* Header */}
      <header className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-5">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 sm:gap-4">
            <div className="flex items-start gap-3 sm:gap-4 w-full sm:w-auto">
              <Button
                variant="outline"
                onClick={() => navigate('/users')}
                className="flex items-center gap-2 shrink-0"
                size="sm"
              >
                <ArrowLeft size={16} />
                <span className="hidden sm:inline">Back</span>
              </Button>
              <div className="flex-1 min-w-0">
                <h1 className="text-lg sm:text-2xl font-bold text-gray-900 truncate">
                  {user.first_name} {user.last_name || user.name}
                </h1>
                <p className="text-xs sm:text-sm text-gray-600 mt-1">User Profile & Details</p>
              </div>
            </div>
            
            <div className="flex flex-col sm:flex-row gap-2 sm:gap-3">
              {/* Refresh Button */}
              <Button
                variant="outline"
                onClick={fetchAllData}
                className="flex items-center gap-2 w-full sm:w-auto text-sm"
                size="sm"
                disabled={loading}
              >
                <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
                <span>Refresh</span>
              </Button>
              
              {/* Download User Data Button */}
              <Button
                variant="outline"
                onClick={handleDownloadUserData}
                className="flex items-center gap-2 w-full sm:w-auto text-sm"
                size="sm"
              >
                <Download size={14} />
                <span>Download Data</span>
              </Button>
              
              {/* Delete User Button */}
              {user.role !== 'admin' && (
                <Button
                  variant="destructive"
                  onClick={handleDeleteClick}
                  className="flex items-center gap-2 w-full sm:w-auto text-sm"
                  size="sm"
                >
                  <Trash2 size={14} />
                  <span>Delete User</span>
                </Button>
              )}
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Last Visited Banner */}
        <Card className="mb-4 sm:mb-6 bg-blue-50 border-blue-200">
          <CardContent className="pt-4 sm:pt-6">
            <div className="flex items-center gap-2 sm:gap-3">
              <Calendar className="w-4 h-4 sm:w-5 sm:h-5 text-blue-600 shrink-0" />
              <div className="min-w-0">
                <p className="text-xs sm:text-sm font-medium text-blue-900">Last Visited</p>
                <p className="text-xs sm:text-sm text-blue-700 mt-1">
                  {user.last_visited ? formatDate(user.last_visited) : formatDate(user.created_at)}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6">
          {/* Left Column - KYC Section */}
          <div className="lg:col-span-1 space-y-4 sm:space-y-6">
            {/* KYC Information Card */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-base sm:text-lg">
                  <User className="w-4 h-4 sm:w-5 sm:h-5" />
                  KYC Information
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3 sm:space-y-4">
                <div>
                  <label className="text-xs sm:text-sm font-medium text-gray-600">First Name</label>
                  <p className="mt-1 text-sm sm:text-base text-gray-900 break-words">{user.first_name || 'N/A'}</p>
                </div>
                <div>
                  <label className="text-xs sm:text-sm font-medium text-gray-600">Last Name</label>
                  <p className="mt-1 text-sm sm:text-base text-gray-900 break-words">{user.last_name || 'N/A'}</p>
                </div>
                <div>
                  <label className="text-xs sm:text-sm font-medium text-gray-600 flex items-center gap-2">
                    <Phone className="w-3 h-3 sm:w-4 sm:h-4" />
                    Contact Number
                  </label>
                  <p className="mt-1 text-sm sm:text-base text-gray-900 break-words">{user.phone || 'N/A'}</p>
                </div>
                <div>
                  <label className="text-xs sm:text-sm font-medium text-gray-600 flex items-center gap-2">
                    <Mail className="w-3 h-3 sm:w-4 sm:h-4" />
                    Email
                  </label>
                  <p className="mt-1 text-sm sm:text-base text-gray-900 break-words">{user.email || 'N/A'}</p>
                </div>
                <div>
                  <label className="text-xs sm:text-sm font-medium text-gray-600 flex items-center gap-2">
                    <MapPin className="w-3 h-3 sm:w-4 sm:h-4" />
                    Address
                  </label>
                  <p className="mt-1 text-sm sm:text-base text-gray-900 break-words">{user.address || 'N/A'}</p>
                </div>
                <div>
                  <label className="text-xs sm:text-sm font-medium text-gray-600">City</label>
                  <p className="mt-1 text-sm sm:text-base text-gray-900 break-words">{user.city || 'N/A'}</p>
                </div>
                <div>
                  <label className="text-xs sm:text-sm font-medium text-gray-600">State</label>
                  <p className="mt-1 text-sm sm:text-base text-gray-900 break-words">{user.state || 'N/A'}</p>
                </div>
                <div>
                  <label className="text-xs sm:text-sm font-medium text-gray-600">Pincode</label>
                  <p className="mt-1 text-sm sm:text-base text-gray-900 break-words">{user.pincode || 'N/A'}</p>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Right Column - EOI & Products */}
          <div className="lg:col-span-2 space-y-4 sm:space-y-6">
            {/* Expression of Interest (EOI) Form Data */}
            {user.interest_form_data && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-base sm:text-lg">
                    <Package className="w-4 h-4 sm:w-5 sm:h-5" />
                    Expression of Interest (EOI) Data
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3 sm:space-y-4">
                  {Object.entries(user.interest_form_data).map(([key, value]) => (
                    <div key={key}>
                      <label className="text-xs sm:text-sm font-medium text-gray-600 capitalize">
                        {key.replace(/_/g, ' ')}
                      </label>
                      <p className="mt-1 text-sm sm:text-base text-gray-900 break-words">
                        {Array.isArray(value) ? value.join(', ') : value?.toString() || 'N/A'}
                      </p>
                    </div>
                  ))}
                </CardContent>
              </Card>
            )}

            {/* Lili Insights Section */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-base sm:text-lg">
                  <MessageSquare className="w-4 h-4 sm:w-5 sm:h-5" />
                  Lili Insights
                </CardTitle>
                <p className="text-xs sm:text-sm text-gray-600 mt-1">
                  AI Concierge conversation summary
                </p>
                {liliDataLoading && (
                  <div className="flex items-center gap-2 text-sm text-blue-600">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                    Loading Lili insights...
                  </div>
                )}
              </CardHeader>
              <CardContent className="space-y-3 sm:space-y-4">
                {/* Metadata */}
                <div className="flex flex-col sm:flex-row gap-2 sm:gap-6 text-xs sm:text-sm text-gray-600">
                  <div className="flex items-center gap-2">
                    <Clock className="w-3 h-3 sm:w-4 sm:h-4" />
                    <span>Last Updated:</span>
                    <span className="font-medium text-gray-900">
                      {liliData.lili_last_updated ? formatDate(liliData.lili_last_updated) : 'Never'}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <MessageSquare className="w-3 h-3 sm:w-4 sm:h-4" />
                    <span>Total Conversations:</span>
                    <span className="font-medium text-gray-900">
                      {liliData.lili_total_conversations || 0}
                    </span>
                  </div>
                </div>

                {/* Summary Content */}
                {/* No Conversations Yet */}
                {(!liliData.lili_conversation_summary || liliData.lili_conversation_summary.trim() === '') && (
                  <div className="text-center py-6">
                    <MessageSquare className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                    <p className="text-gray-500 text-sm">No conversations yet</p>
                    <p className="text-gray-400 text-xs mt-1">
                      Summary will appear when user chats with Lili AI Concierge
                    </p>
                  </div>
                )}

                {/* No Consent Message */}
                {liliData.lili_conversation_summary && 
                 liliData.lili_conversation_summary.includes('No Consent') && (
                  <div className="flex items-start gap-3 p-4 bg-gray-50 rounded-lg border border-gray-200">
                    <X className="w-5 h-5 text-gray-400 flex-shrink-0" />
                    <div>
                      <p className="text-gray-700 text-sm font-medium">Privacy Protected</p>
                      <p className="text-gray-600 text-xs mt-1">
                        {liliData.lili_conversation_summary}
                      </p>
                    </div>
                  </div>
                )}

                {/* Full Summary with Bullet Points */}
                {liliData.lili_conversation_summary && 
                 !liliData.lili_conversation_summary.includes('No Consent') &&
                 liliData.lili_conversation_summary.trim() !== '' && (
                  <div className="space-y-2">
                    <pre className="whitespace-pre-wrap font-sans text-sm text-gray-700 leading-relaxed">
                      {liliData.lili_conversation_summary}
                    </pre>
                    
                    {/* Business Value Indicators (if conversations exist) */}
                    {liliData.lili_total_conversations > 0 && (
                      <div className="mt-4 pt-3 border-t border-gray-200">
                        <div className="flex items-center gap-2 text-xs text-gray-600">
                          <span className="font-medium">Lead Quality:</span>
                          <span className={`px-2 py-1 rounded-full ${
                            liliData.lili_total_conversations >= 5 
                              ? 'bg-green-100 text-green-700' 
                              : liliData.lili_total_conversations >= 3
                              ? 'bg-yellow-100 text-yellow-700'
                              : 'bg-gray-100 text-gray-700'
                          }`}>
                            {liliData.lili_total_conversations >= 5 ? 'High Engagement' : 
                             liliData.lili_total_conversations >= 3 ? 'Medium Engagement' : 
                             'Low Engagement'}
                          </span>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Enhanced Products Interested Section */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-base sm:text-lg">
                  <Package className="w-4 h-4 sm:w-5 sm:h-5" />
                  Products Interested
                </CardTitle>
                <p className="text-xs sm:text-sm text-gray-600 mt-1">
                  Products the user has shown interest in through cart additions, EOI submissions, and other interactions
                </p>
                {productInterestsLoading && (
                  <div className="flex items-center gap-2 text-sm text-blue-600">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                    Loading latest interests...
                  </div>
                )}
              </CardHeader>
              <CardContent className="space-y-2 sm:space-y-3">
                {(() => {
                  const interestedProducts = getInterestedProducts();
                  
                  if (interestedProducts.length === 0) {
                    return (
                      <div className="text-center py-6">
                        <Package className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                        <p className="text-gray-500 text-sm">No product interests recorded yet</p>
                        <p className="text-gray-400 text-xs mt-1">
                          Interests will appear when user adds products to cart or submits EOI forms
                        </p>
                      </div>
                    );
                  }
                  
                  // Sort by timestamp (most recent first)
                  const sortedProducts = interestedProducts.sort((a, b) => 
                    new Date(b.timestamp) - new Date(a.timestamp)
                  );
                  
                  return sortedProducts.map((product, index) => (
                    <div key={index} className="p-3 sm:p-4 bg-gradient-to-r from-green-50 to-blue-50 rounded-lg border border-green-200">
                      <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-2">
                        <div className="flex-1">
                          <div className="flex items-start gap-2 flex-wrap">
                            <span className="text-sm sm:text-base font-medium text-gray-900">
                              {product.name}
                            </span>
                            <span className={`px-2 py-1 text-xs rounded-full ${
                              product.type === 'asset' 
                                ? 'bg-purple-100 text-purple-700' 
                                : product.type === 'package'
                                ? 'bg-orange-100 text-orange-700'
                                : 'bg-blue-100 text-blue-700'
                            }`}>
                              {product.type === 'asset' ? 'Individual Asset' : 
                               product.type === 'package' ? 'Package' :
                               product.type === 'individual' ? 'Individual Cars' : product.type}
                            </span>
                          </div>
                          
                          <div className="flex flex-col sm:flex-row gap-1 sm:gap-4 mt-2 text-xs text-gray-600">
                            <span className="flex items-center gap-1">
                              <Clock size={12} />
                              {formatTimestamp(product.timestamp)}
                            </span>
                            <span className="flex items-center gap-1">
                              <Check size={12} />
                              {formatInterestType(product.interestType)}
                            </span>
                          </div>
                          
                          {/* Enhanced Additional Data Display for Cart Information */}
                          {product.additionalData && Object.keys(product.additionalData).length > 0 && (
                            <div className="mt-2 text-xs text-gray-500 flex flex-wrap gap-3">
                              {product.additionalData.price_shown && (
                                <span className="flex items-center gap-1">
                                  <span>💰</span>
                                  Price: ₹{(product.additionalData.price_shown / 100000).toFixed(1)}L
                                </span>
                              )}
                              {product.additionalData.tickets_count && (
                                <span className="flex items-center gap-1 font-medium text-blue-600">
                                  <Ticket size={14} />
                                  {product.additionalData.tickets_count} Ticket{product.additionalData.tickets_count > 1 ? 's' : ''}
                                </span>
                              )}
                              {product.additionalData.tickets_interested && (
                                <span className="flex items-center gap-1">
                                  <span>🎫</span>
                                  Tickets: {product.additionalData.tickets_interested}
                                </span>
                              )}
                              {typeof product.additionalData.brochure_downloaded !== 'undefined' && (
                                <span className={`flex items-center gap-1 ${
                                  product.additionalData.brochure_downloaded 
                                    ? 'text-green-600 font-medium' 
                                    : 'text-gray-400'
                                }`}>
                                  <FileText size={14} />
                                  Brochure: {product.additionalData.brochure_downloaded ? 'Downloaded' : 'Not Downloaded'}
                                </span>
                              )}
                              {product.additionalData.items_count && (
                                <span className="flex items-center gap-1">
                                  <span>📦</span>
                                  Items: {product.additionalData.items_count}
                                </span>
                              )}
                              {product.additionalData.vehicles_included && product.additionalData.vehicles_included.length > 0 && (
                                <span className="flex items-center gap-1">
                                  <span>🚗</span>
                                  Vehicles: {product.additionalData.vehicles_included.join(', ')}
                                </span>
                              )}
                              {product.additionalData.investment_range && (
                                <span className="flex items-center gap-1">
                                  <span>💼</span>
                                  Investment: {product.additionalData.investment_range}
                                </span>
                              )}
                            </div>
                          )}
                        </div>
                        
                        <div className="flex items-center gap-2 mt-2 sm:mt-0">
                          <span className="flex items-center gap-2 text-green-600 font-medium text-sm">
                            <Check size={16} className="w-4 h-4" />
                            <span>Interested</span>
                          </span>
                        </div>
                      </div>
                    </div>
                  ));
                })()}
              </CardContent>
            </Card>

            {/* List of Products Purchased */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-base sm:text-lg">
                  <ShoppingCart className="w-4 h-4 sm:w-5 sm:h-5" />
                  Products Purchased
                </CardTitle>
                <p className="text-xs sm:text-sm text-gray-600 mt-1">
                  Actual purchases and transactions
                </p>
              </CardHeader>
              <CardContent>
                {/* Individual Asset Tickets */}
                {userPurchases?.assets?.length > 0 && (
                  <div className="mb-6">
                    <h4 className="text-sm font-semibold text-gray-700 mb-3">Individual Asset Tickets</h4>
                    <div className="space-y-3">
                      {userPurchases.assets?.map((purchase) => (
                        <div key={purchase.asset_id} className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                          <div className="flex items-start justify-between">
                            <div>
                              <p className="font-medium text-gray-900">{purchase.asset_name}</p>
                              <p className="text-sm text-gray-600 mt-1">
                                Tickets Purchased: <span className="font-semibold">{purchase.tickets_count}</span>
                              </p>
                              <p className="text-sm text-gray-600">
                                Total Amount: <span className="font-semibold">{formatCurrency(purchase.total_amount)}</span>
                              </p>
                            </div>
                            <div className="text-right">
                              <span className={`px-2 py-1 text-xs rounded-full ${
                                purchase.payment_status === 'paid' 
                                  ? 'bg-green-100 text-green-800' 
                                  : purchase.payment_status === 'pending'
                                  ? 'bg-yellow-100 text-yellow-800'
                                  : 'bg-red-100 text-red-800'
                              }`}>
                                {purchase.payment_status}
                              </span>
                              {purchase.transaction_id && (
                                <p className="text-xs text-gray-500 mt-2">
                                  TXN: {purchase.transaction_id}
                                </p>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Curated Packages */}
                {userPurchases?.packages?.length > 0 && (
                  <div>
                    <h4 className="text-sm font-semibold text-gray-700 mb-3">Curated Packages</h4>
                    <div className="space-y-3">
                      {userPurchases.packages?.map((purchase) => (
                        <div key={purchase.package_id} className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                          <div className="flex items-start justify-between">
                            <div>
                              <p className="font-medium text-gray-900">{purchase.package_name}</p>
                              <p className="text-sm text-gray-600 mt-1">
                                Total Amount: <span className="font-semibold">{formatCurrency(purchase.total_amount)}</span>
                              </p>
                            </div>
                            <div className="text-right">
                              <span className={`px-2 py-1 text-xs rounded-full ${
                                purchase.payment_status === 'paid' 
                                  ? 'bg-green-100 text-green-800' 
                                  : purchase.payment_status === 'pending'
                                  ? 'bg-yellow-100 text-yellow-800'
                                  : 'bg-red-100 text-red-800'
                              }`}>
                                {purchase.payment_status}
                              </span>
                              {purchase.transaction_id && (
                                <p className="text-xs text-gray-500 mt-2">
                                  TXN: {purchase.transaction_id}
                                </p>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* No purchases message */}
                {(!userPurchases?.assets?.length && !userPurchases?.packages?.length) && (
                  <div className="text-center py-8 text-gray-500">
                    No purchases yet
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </main>

      {/* Delete User Dialog - Simplified Flow */}
      <Dialog open={showDeleteDialog} onOpenChange={(open) => !deleting && setShowDeleteDialog(open)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle className="w-5 h-5" />
              {!showFinalConfirm ? 'Delete User - Enter Password' : 'Delete User - Final Confirmation'}
            </DialogTitle>
            <DialogDescription>
              {!showFinalConfirm ? (
                <>
                  You are about to delete <strong>{user?.first_name} {user?.last_name}</strong>.
                  Please enter your admin password to continue.
                </>
              ) : (
                <>
                  Are you sure you want to permanently delete this user? This action cannot be undone.
                </>
              )}
            </DialogDescription>
          </DialogHeader>

          <div className="py-4 space-y-4">
            {/* Password Entry */}
            {!showFinalConfirm && (
              <div>
                <Label htmlFor="admin-password" className="text-sm font-medium">
                  Admin Password <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="admin-password"
                  type="password"
                  value={adminPassword}
                  onChange={(e) => {
                    setAdminPassword(e.target.value);
                    setDeleteError('');
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && adminPassword) {
                      handlePasswordSubmit();
                    }
                  }}
                  placeholder="Enter your admin password"
                  className="mt-2"
                  disabled={deleting}
                  autoFocus
                />
              </div>
            )}

            {/* Final Confirmation */}
            {showFinalConfirm && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <p className="text-sm text-red-900 font-medium">
                  User: {user?.first_name} {user?.last_name}
                </p>
                <p className="text-sm text-red-700">
                  Email: {user?.email}
                </p>
                <p className="text-sm text-red-700 mt-2 font-semibold">
                  ⚠️ This action is IRREVERSIBLE!
                </p>
                <p className="text-xs text-red-600 mt-2">
                  All user data, purchases, and history will be permanently removed.
                </p>
              </div>
            )}

            {/* Error Message */}
            {deleteError && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                <p className="text-sm text-red-600 font-medium">{deleteError}</p>
              </div>
            )}
          </div>

          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button 
              variant="outline" 
              onClick={resetDeleteFlow}
              disabled={deleting}
              className="w-full sm:w-auto"
            >
              Cancel
            </Button>
            
            {!showFinalConfirm ? (
              <Button 
                variant="destructive" 
                onClick={handlePasswordSubmit}
                disabled={!adminPassword || deleting}
                className="w-full sm:w-auto"
              >
                Continue
              </Button>
            ) : (
              <Button 
                variant="destructive" 
                onClick={handleFinalDelete}
                disabled={deleting}
                className="w-full sm:w-auto"
              >
                {deleting ? (
                  <>
                    <span className="animate-spin mr-2">⏳</span>
                    Deleting...
                  </>
                ) : (
                  '🗑️ Delete User Permanently'
                )}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default UserDetailPage;
